import {MithrilComponent} from '@bitstillery/mithril'
import m from '@bitstillery/mithril'
import {Button, CollectionItems, CollectionView, Link} from '@bitstillery/common/components'
import {classes} from '@bitstillery/common/lib/utils'
import {state} from '@bitstillery/mithril'

import type {CollectionProxy, CollectionRow} from '@bitstillery/common/lib/collection'

interface CollectionCarouselAttrs<Row extends CollectionRow = CollectionRow> {
    className?: string
    collection: CollectionProxy<Row>
    icon?: string
    item: (row: Row, opts: {global_index: number}) => m.Children
    /** Funnel home: title row like Popular categories; panel surface; scroll buttons at left/right of the strip. */
    layout?: 'default' | 'funnel'
    title?: string | undefined
    /** Deep link to the full collection; turns the heading into a link. */
    title_link?: string | undefined
}

/** Pointer travel that separates a click on a card from a drag of the strip. */
const DRAG_CLICK_THRESHOLD_PX = 5

export class CollectionCarousel<Row extends CollectionRow = CollectionRow> extends MithrilComponent<
    CollectionCarouselAttrs<Row>
> {
    private drag_container?: HTMLDivElement
    private scroll_target?: HTMLElement | null
    private is_dragging = false
    private drag_start_x = 0
    private start_scroll_left = 0
    /** Set once a press travels past {@link DRAG_CLICK_THRESHOLD_PX}, so the release is read as a drag. */
    private drag_moved = false

    /**
     * Swallow the click that ends a drag: the strip is dragged by the cards themselves, and those cards
     * carry their own actions (a title deep link, the photo carousel). Without this, scrolling the strip
     * by grabbing a card navigates away on release. Capture phase, so the card's own handler never runs.
     */
    private on_click_capture = (e: MouseEvent) => {
        if (!this.drag_moved) {
            return
        }
        this.drag_moved = false
        e.preventDefault()
        e.stopPropagation()
    }

    data = state({
        is_left_disabled: true,
        is_right_disabled: false,
    })

    /** Stored so it can be detached on remove; an anonymous listener could never be removed. */
    private on_scroll = () => this.schedule_button_states()
    /** rAF handle for a pending measurement, so bursts of scroll/resize events collapse into one. */
    private measure_frame: number | null = null
    private resize_observer?: ResizeObserver
    /** Last measured offset; lets an unchanged scroll position skip the layout-forcing reads below. */
    private last_scroll_left: number | null = null

    /**
     * Watch the scroll container and its item strip, so content or viewport size changes still update the
     * buttons now that `onupdate` no longer drives them.
     *
     * The item strip matters as much as the container: adding or removing items changes its width, which
     * is what decides whether the right-hand button should be enabled.
     */
    private observe_size(scroll_target: HTMLElement) {
        if (typeof ResizeObserver === 'undefined') {
            return
        }
        this.resize_observer = new ResizeObserver(() => {
            // Geometry changed, so the cached offset is no longer a valid reason to skip.
            this.last_scroll_left = null
            this.schedule_button_states()
        })
        this.resize_observer.observe(scroll_target)
        const items = scroll_target.querySelector('.c-collection-items')
        if (items) {
            this.resize_observer.observe(items)
        }
    }

    /**
     * Measure at most once per frame.
     *
     * `update_button_states` reads `scrollWidth`/`clientWidth`, which force layout. It used to be driven
     * by an unthrottled scroll listener AND `onupdate` — so on every redraw, several times per frame —
     * and was the largest app-level entry in a scroll profile at ~41ms.
     *
     * It is now driven only by things that can actually change the answer: scrolling, and a resize of the
     * container or the item strip. A redraw that changes neither does no measuring at all. Batching per
     * frame is safe because these signals only need to be correct by the next paint, and they dedupe on
     * assignment, so a measurement that changes nothing costs no redraw.
     */
    private schedule_button_states(): void {
        if (this.measure_frame !== null || typeof requestAnimationFrame === 'undefined') {
            return
        }
        this.measure_frame = requestAnimationFrame(() => {
            this.measure_frame = null
            this.update_button_states()
        })
    }

    /**
     * Detach the scroll listener and drop any pending measurement. Without this a queued rAF would fire
     * against a removed component and write to its signals, and the listener would outlive the carousel.
     */
    override onremove() {
        if (this.measure_frame !== null && typeof cancelAnimationFrame !== 'undefined') {
            cancelAnimationFrame(this.measure_frame)
            this.measure_frame = null
        }
        this.scroll_target?.removeEventListener('scroll', this.on_scroll)
        this.drag_container?.removeEventListener('click', this.on_click_capture, true)
        this.resize_observer?.disconnect()
    }

    private update_button_states(): void {
        if (!this.scroll_target) {
            return
        }

        const scroll_left = this.scroll_target.scrollLeft
        // Nothing scrolled since the last measurement, so the flags cannot have changed — and skipping
        // here avoids the two layout-forcing reads below. Content/size changes come in via `onupdate`,
        // which resets this.
        if (scroll_left === this.last_scroll_left) {
            return
        }
        this.last_scroll_left = scroll_left
        const scroll_width = this.scroll_target.scrollWidth
        const client_width = this.scroll_target.clientWidth

        // Account for small floating point differences
        const threshold = 1

        this.data.is_left_disabled = scroll_left <= threshold
        this.data.is_right_disabled = scroll_left >= scroll_width - client_width - threshold
    }

    private get_page_delta(direction: 'left' | 'right'): number {
        if (!this.scroll_target) {
            return 0
        }
        const viewport_width = this.scroll_target.getBoundingClientRect().width

        // Find the grid container and a representative item
        const items_container = this.scroll_target.querySelector<HTMLElement>('.c-collection-items')
        const first_item = items_container?.querySelector<HTMLElement>(':scope > *')
        if (!items_container || !first_item) {
            return viewport_width
        }

        const item_width = first_item.getBoundingClientRect().width
        const styles = getComputedStyle(items_container)
        const gap_str = styles.columnGap || styles.gap || '0'
        const gap_px = Number.parseFloat(gap_str) || 0

        const item_full_width = item_width + gap_px
        if (item_full_width <= 0) {
            return viewport_width
        }

        let items_per_page = Math.floor(viewport_width / item_full_width)
        if (items_per_page < 1) {
            items_per_page = 1
        }

        const delta = items_per_page * item_full_width
        return direction === 'right' ? delta : -delta
    }

    private render_scroll_button(side: 'left' | 'right', funnel: boolean) {
        const is_left = side === 'left'
        const is_disabled = is_left ? this.data.is_left_disabled : this.data.is_right_disabled
        return (
            <Button
                className={classes('btn-scroll', side, {
                    'btn-scroll-funnel-edge': funnel,
                    disabled: is_disabled,
                })}
                disabled={is_disabled}
                icon={is_left ? 'chevronLeft' : 'chevronRight'}
                type='brand'
                onclick={(e) => {
                    e.stopPropagation()
                    if (!this.scroll_target) {
                        return
                    }
                    const delta = this.get_page_delta(is_left ? 'left' : 'right')
                    this.scroll_target.scrollBy({behavior: 'smooth', left: delta})
                }}
                variant='toggle'
            />
        )
    }

    private render_drag_scroll(vnode: m.Vnode<CollectionCarouselAttrs<Row>>) {
        return (
            <div
                className='drag-scroll'
                oncreate={(create_vnode: m.VnodeDOM) => {
                    const dom = create_vnode.dom
                    if (!(dom instanceof HTMLDivElement)) {
                        return
                    }
                    this.drag_container = dom
                    this.scroll_target = dom.querySelector<HTMLElement>('.c-collection-view')

                    // Capture, so a drag-ending click is swallowed before the card it lands on sees it.
                    this.drag_container.addEventListener('click', this.on_click_capture, true)

                    if (this.scroll_target) {
                        // Passive: this listener only measures, it never calls preventDefault, and
                        // saying so lets the browser scroll without waiting on it.
                        this.scroll_target.addEventListener('scroll', this.on_scroll, {passive: true})
                        this.observe_size(this.scroll_target)
                    }
                }}
                onmousedown={(e: MouseEvent) => {
                    if (!this.scroll_target) {
                        return
                    }
                    this.is_dragging = true
                    this.drag_moved = false
                    this.drag_start_x = e.clientX
                    this.start_scroll_left = this.scroll_target.scrollLeft
                    this.drag_container?.classList.add('dragging')
                    e.preventDefault()
                }}
                onmousemove={(e: MouseEvent) => {
                    if (!this.is_dragging || !this.scroll_target) {
                        return
                    }
                    const delta_x = e.clientX - this.drag_start_x
                    if (Math.abs(delta_x) > DRAG_CLICK_THRESHOLD_PX) {
                        this.drag_moved = true
                    }
                    this.scroll_target.scrollLeft = this.start_scroll_left - delta_x
                }}
                onmouseup={() => {
                    this.is_dragging = false
                    this.drag_container?.classList.remove('dragging')
                }}
                onmouseleave={() => {
                    this.is_dragging = false
                    this.drag_container?.classList.remove('dragging')
                }}
            >
                <CollectionView mode='grid'>
                    <CollectionItems collection={vnode.attrs.collection} item={vnode.attrs.item} />
                </CollectionView>
            </div>
        )
    }

    view(vnode: m.Vnode<CollectionCarouselAttrs<Row>>) {
        return (
            <div className={classes('c-collection-carousel', vnode.attrs.className)}>
                <div className='carousel-header'>
                    {vnode.attrs.title_link ? (
                        <Link className='title' href={vnode.attrs.title_link} tabindex={0} type='default'>
                            {vnode.attrs.title}
                        </Link>
                    ) : (
                        <div className='title'>{vnode.attrs.title}</div>
                    )}
                    <div className='btn-container'>
                        {this.render_scroll_button('left', false)}
                        {this.render_scroll_button('right', false)}
                    </div>
                </div>
                <div className='carousel-container'>{this.render_drag_scroll(vnode)}</div>
            </div>
        )
    }
}
